import { Plus, Trash2, Pencil, Check, X, Sparkles } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";

import { MemoryManager } from "@/components/memories/MemoryManager";
import { AppBreadcrumb } from "@/components/navigation/AppBreadcrumb";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ColorPicker } from "@/components/ui/color-picker";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useBoardDetailState } from "@/hooks/useBoardDetailState";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { formatOklchForDisplay } from "@/lib/colorUtils";

const BoardDetail = () => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const {
    state,
    dispatch,
    isInvoking,
    addPersona,
    deletePersona,
    addProject,
    startEditingName,
    cancelEditingName,
    saveEditedName,
    startEditingDescription,
    cancelEditingDescription,
    saveEditedDescription,
    startEditingColor,
    cancelEditingColor,
    saveEditedColor,
    startEditingPersona,
    updatePersona,
    handleRegenerateClick,
    handleRegeneratePersona,
    handleRerollFromEdit,
    handleDeleteBoard,
    executeDeleteBoard,
  } = useBoardDetailState(boardId);
  
  // Enable keyboard shortcuts for back navigation
  useKeyboardNavigation({
    onBack: () => navigate("/dashboard"),
  });

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppBreadcrumb />

      <div className="border-b border-border bg-background">
        <div className="container mx-auto px-4 py-3 flex justify-end gap-2">
          <MemoryManager
            boardId={boardId}
            scopeLabel={state.board?.name || "this board"}
          />
          <Button variant="destructive" size="sm" onClick={handleDeleteBoard}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Board
          </Button>
        </div>
      </div>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-8">
          {state.isEditingName ? (
            <div className="flex items-center gap-2 mb-2">
              <Input
                value={state.editedName}
                onChange={(e) => dispatch({ type: 'SET_EDITED_NAME', payload: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveEditedName();
                  if (e.key === "Escape") cancelEditingName();
                }}
                className="text-3xl font-semibold h-12 max-w-md"
                autoFocus
              />
              <Button size="icon" variant="ghost" onClick={saveEditedName}>
                <Check className="h-5 w-5 text-green-600" />
              </Button>
              <Button size="icon" variant="ghost" onClick={cancelEditingName}>
                <X className="h-5 w-5 text-destructive" />
              </Button>
            </div>
          ) : (
            <div
              className="flex items-center gap-2 mb-2 group cursor-pointer"
              onMouseEnter={() => dispatch({ type: 'SET_IS_HOVERING_NAME', payload: true })}
              onMouseLeave={() => dispatch({ type: 'SET_IS_HOVERING_NAME', payload: false })}
              onDoubleClick={startEditingName}
            >
              <h1 className="text-3xl font-semibold">{state.board?.name}</h1>
              {state.isHoveringName && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={startEditingName}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
          {state.isEditingDescription ? (
            <div className="flex items-start gap-2 mb-2">
              <Textarea
                value={state.editedDescription}
                onChange={(e) => dispatch({ type: 'SET_EDITED_DESCRIPTION', payload: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Escape") cancelEditingDescription();
                }}
                className="text-muted-foreground resize-none"
                rows={2}
                autoFocus
              />
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={saveEditedDescription}>
                  <Check className="h-5 w-5 text-green-600" />
                </Button>
                <Button size="icon" variant="ghost" onClick={cancelEditingDescription}>
                  <X className="h-5 w-5 text-destructive" />
                </Button>
              </div>
            </div>
          ) : (
            <div
              className="flex items-start gap-2 group cursor-pointer"
              onMouseEnter={() => dispatch({ type: 'SET_IS_HOVERING_DESCRIPTION', payload: true })}
              onMouseLeave={() => dispatch({ type: 'SET_IS_HOVERING_DESCRIPTION', payload: false })}
              onDoubleClick={startEditingDescription}
            >
              <p className="text-muted-foreground flex-1">
                {state.board?.description || "No description"}
              </p>
              {state.isHoveringDescription && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6"
                  onClick={startEditingDescription}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
          
          {/* Color Picker */}
          <div className="mt-4">
            {state.isEditingColor ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Board Color</Label>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={saveEditedColor}>
                      <Check className="h-4 w-4 text-green-600" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={cancelEditingColor}>
                      <X className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                <ColorPicker
                  value={state.editedColor}
                  onChange={(color) => dispatch({ type: 'SET_EDITED_COLOR', payload: color })}
                />
              </div>
            ) : (
              <div
                className="flex items-center gap-2 group cursor-pointer"
                onMouseEnter={() => dispatch({ type: 'SET_IS_HOVERING_COLOR', payload: true })}
                onMouseLeave={() => dispatch({ type: 'SET_IS_HOVERING_COLOR', payload: false })}
                onClick={startEditingColor}
              >
                <Label className="text-sm text-muted-foreground">Board Color:</Label>
                <div
                  className="w-8 h-8 rounded border border-border"
                  style={{ backgroundColor: state.board?.color || "oklch(0.65 0.2 180)" }}
                />
                <div className="px-2 py-1 rounded text-xs font-mono text-muted-foreground">
                  {formatOklchForDisplay(state.board?.color || "oklch(0.65 0.2 180)")}
                </div>
                {state.isHoveringColor && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6"
                    onClick={startEditingColor}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        <section className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">Board Members</h2>
            <Dialog open={state.isAddingPersona} onOpenChange={(open) => dispatch({ type: 'SET_IS_ADDING_PERSONA', payload: open })}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Persona
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Persona</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="role_name">Role Name</Label>
                    <Input
                      id="role_name"
                      value={state.newPersona.role_name}
                      onChange={(e) => dispatch({ type: 'UPDATE_NEW_PERSONA_FIELD', payload: { field: 'role_name', value: e.target.value } })}
                      placeholder="e.g., CPXO"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mission">Mission</Label>
                    <Textarea
                      id="mission"
                      value={state.newPersona.mission}
                      onChange={(e) => dispatch({ type: 'UPDATE_NEW_PERSONA_FIELD', payload: { field: 'mission', value: e.target.value } })}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="principles">Principles</Label>
                    <Textarea
                      id="principles"
                      value={state.newPersona.principles}
                      onChange={(e) => dispatch({ type: 'UPDATE_NEW_PERSONA_FIELD', payload: { field: 'principles', value: e.target.value } })}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="competencies">Competencies</Label>
                    <Textarea
                      id="competencies"
                      value={state.newPersona.competencies}
                      onChange={(e) => dispatch({ type: 'UPDATE_NEW_PERSONA_FIELD', payload: { field: 'competencies', value: e.target.value } })}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="key_relationships">Key Relationships</Label>
                    <Textarea
                      id="key_relationships"
                      value={state.newPersona.key_relationships}
                      onChange={(e) => dispatch({ type: 'UPDATE_NEW_PERSONA_FIELD', payload: { field: 'key_relationships', value: e.target.value } })}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="typical_questions">Typical Questions</Label>
                    <Textarea
                      id="typical_questions"
                      value={state.newPersona.typical_questions}
                      onChange={(e) => dispatch({ type: 'UPDATE_NEW_PERSONA_FIELD', payload: { field: 'typical_questions', value: e.target.value } })}
                      rows={2}
                    />
                  </div>
                  <Button onClick={addPersona} className="w-full">Add Persona</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Dialog open={state.isEditingPersona} onOpenChange={(open) => {
            dispatch({ type: 'SET_IS_EDITING_PERSONA', payload: open });
            if (!open) dispatch({ type: 'SET_GENERATED_PERSONA_DATA', payload: null });
          }}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {state.generatedPersonaData ? "Review Regenerated Persona" : "Edit Persona"}
                </DialogTitle>
                {state.generatedPersonaData && (
                  <DialogDescription>
                    Review the changes and save them, or regenerate with more specific instructions.
                  </DialogDescription>
                )}
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit_role_name">Role Name</Label>
                  <Input
                    id="edit_role_name"
                    value={state.editingPersona?.role_name || ""}
                    onChange={(e) => dispatch({ type: 'UPDATE_EDITING_PERSONA_FIELD', payload: { field: 'role_name', value: e.target.value } })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_mission">Mission</Label>
                  <Textarea
                    id="edit_mission"
                    value={state.editingPersona?.mission || ""}
                    onChange={(e) => dispatch({ type: 'UPDATE_EDITING_PERSONA_FIELD', payload: { field: 'mission', value: e.target.value } })}
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_principles">Principles</Label>
                  <Textarea
                    id="edit_principles"
                    value={state.editingPersona?.principles || ""}
                    onChange={(e) => dispatch({ type: 'UPDATE_EDITING_PERSONA_FIELD', payload: { field: 'principles', value: e.target.value } })}
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_competencies">Competencies</Label>
                  <Textarea
                    id="edit_competencies"
                    value={state.editingPersona?.competencies || ""}
                    onChange={(e) => dispatch({ type: 'UPDATE_EDITING_PERSONA_FIELD', payload: { field: 'competencies', value: e.target.value } })}
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_key_relationships">Key Relationships</Label>
                  <Textarea
                    id="edit_key_relationships"
                    value={state.editingPersona?.key_relationships || ""}
                    onChange={(e) => dispatch({ type: 'UPDATE_EDITING_PERSONA_FIELD', payload: { field: 'key_relationships', value: e.target.value } })}
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_typical_questions">Typical Questions</Label>
                  <Textarea
                    id="edit_typical_questions"
                    value={state.editingPersona?.typical_questions || ""}
                    onChange={(e) => dispatch({ type: 'UPDATE_EDITING_PERSONA_FIELD', payload: { field: 'typical_questions', value: e.target.value } })}
                    rows={2}
                  />
                </div>
                <div className="flex gap-2">
                  {state.generatedPersonaData && (
                    <Button onClick={handleRerollFromEdit} variant="outline" className="flex-1">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Regenerate Again
                    </Button>
                  )}
                  <Button onClick={updatePersona} className={state.generatedPersonaData ? "flex-1" : "w-full"}>
                    Save Changes
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {state.personas.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No personas yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {state.personas.map((persona) => (
                <Card key={persona.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{persona.role_name}</CardTitle>
                        <CardDescription className="mt-2">{persona.mission}</CardDescription>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRegenerateClick(persona)}
                          title="Regenerate with AI"
                        >
                          <Sparkles className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditingPersona(persona)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deletePersona(persona.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">Projects</h2>
            <Dialog open={state.isAddingProject} onOpenChange={(open) => dispatch({ type: 'SET_IS_ADDING_PROJECT', payload: open })}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Project
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Project</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="project_name">Project Name</Label>
                    <Input
                      id="project_name"
                      value={state.newProjectName}
                      onChange={(e) => dispatch({ type: 'SET_NEW_PROJECT_NAME', payload: e.target.value })}
                      placeholder="Enter project name"
                    />
                  </div>
                  <Button onClick={addProject} className="w-full">Create Project</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {state.projects.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No projects yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {state.projects.map((project) => (
                <Card
                  key={project.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <CardHeader>
                    <CardTitle>{project.name}</CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>

      <Dialog open={state.isRegenerateDialogOpen} onOpenChange={(open) => dispatch({ type: 'SET_IS_REGENERATE_DIALOG_OPEN', payload: open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate {state.regeneratingPersona?.role_name}</DialogTitle>
            <DialogDescription>
              Describe what you'd like to change about this persona. The AI will regenerate it with your feedback while considering all other board members.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="regenerate_prompt">What would you like to change?</Label>
              <Textarea
                id="regenerate_prompt"
                value={state.regeneratePrompt}
                onChange={(e) => dispatch({ type: 'SET_REGENERATE_PROMPT', payload: e.target.value })}
                placeholder="e.g., Make them more focused on technical implementation, add expertise in cloud architecture..."
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => dispatch({ type: 'SET_IS_REGENERATE_DIALOG_OPEN', payload: false })}>
                Cancel
              </Button>
              <Button onClick={handleRegeneratePersona} disabled={isInvoking}>
                {isInvoking ? (
                  <>Regenerating...</>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Regenerate
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={state.showDeleteDialog} onOpenChange={(open) => dispatch({ type: 'SET_SHOW_DELETE_DIALOG', payload: open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Board</AlertDialogTitle>
            <AlertDialogDescription>
              This board has {state.projectsToHandle.length} project(s). What would you like to do with them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            {state.availableBoards.length > 0 ? (
              <div className="space-y-4">
                <div>
                  <Label>Move projects to another board:</Label>
                  <Select
                    value={state.selectedDestinationBoard || ""}
                    onValueChange={(value) => dispatch({ type: 'SET_SELECTED_DESTINATION_BOARD', payload: value || null })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a board or leave as orphaned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Leave as orphaned</SelectItem>
                      {state.availableBoards.map((board) => (
                        <SelectItem key={board.id} value={board.id}>
                          {board.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Projects will be orphaned (removed from this board but not deleted).
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeDeleteBoard}>
              Delete Board
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BoardDetail;
